import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

const weak = (p) => p.length < 8 || !/[A-Za-z]/.test(p) || !/\d/.test(p);

function NewPasswordFields({ pw, setPw, confirm, setConfirm }) {
  return <>
    <div><Label>New password</Label><Input type="password" className="mt-1" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="pw-new-input" /></div>
    <div><Label>Repeat new password</Label><Input type="password" className="mt-1" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pw-confirm-input" /></div>
    <p className="text-xs text-muted-foreground">At least 8 characters, with letters and numbers.</p>
  </>;
}

export function ChangeOwnPassword({ onDone }) {
  const [cur, setCur] = useState(""); const [pw, setPw] = useState(""); const [confirm, setConfirm] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (weak(pw)) return toast.error("Password must be at least 8 characters with letters and numbers");
    if (pw !== confirm) return toast.error("The two new passwords don't match");
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: cur, new_password: pw }); toast.success("Password changed"); setCur(""); setPw(""); setConfirm(""); onDone?.(); }
    catch (ex) { toast.error(apiError(ex.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 max-w-sm" data-testid="change-password-form">
      <div><Label>Current password</Label><Input type="password" className="mt-1" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="pw-current-input" /></div>
      <NewPasswordFields pw={pw} setPw={setPw} confirm={confirm} setConfirm={setConfirm} />
      <Button type="submit" disabled={busy || !cur || !pw} data-testid="pw-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save new password"}</Button>
    </form>
  );
}

export function SetUserPassword({ target }) {
  const [open, setOpen] = useState(false); const [pw, setPw] = useState(""); const [confirm, setConfirm] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (weak(pw)) return toast.error("Password must be at least 8 characters with letters and numbers");
    if (pw !== confirm) return toast.error("The two passwords don't match");
    setBusy(true);
    try { await api.post(`/users/${target.id}/password`, { new_password: pw }); toast.success(`Password changed for ${target.name}`); setOpen(false); setPw(""); setConfirm(""); }
    catch (ex) { toast.error(apiError(ex.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" title="Change password" data-testid="user-password-btn"><KeyRound className="h-4 w-4" /></Button></DialogTrigger>
      <DialogContent className="max-w-sm" data-testid="user-password-dialog">
        <DialogHeader><DialogTitle>Change password · {target.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <NewPasswordFields pw={pw} setPw={setPw} confirm={confirm} setConfirm={setConfirm} />
          <p className="text-xs text-muted-foreground">They will be signed out everywhere and must use this new password.</p>
          <Button type="submit" className="w-full" disabled={busy || !pw} data-testid="user-password-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change password"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ForcePasswordChange({ user, onDone, onLogout }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 p-4" data-testid="force-password-page">
      <div className="bg-card border rounded-xl p-6 w-full max-w-md shadow-sm">
        <img src="/omi-logo.webp" alt="OMI Fintech Pvt Ltd" className="h-14 w-auto mb-4" />
        <h1 className="text-xl font-bold">Set your own password</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-4">Welcome {user.name}. Your account was created with a temporary password. Please choose your own before you continue.</p>
        <ChangeOwnPassword onDone={onDone} />
        <button className="text-xs text-muted-foreground underline mt-4" onClick={onLogout} data-testid="force-password-logout">Sign out</button>
      </div>
    </div>
  );
}
